import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import Page from 'src/components/Page';
import { useAuth } from 'src/context/auth/AuthContext';
import {
  PAYROLL_CSV_TEMPLATE,
  downloadTextFile,
  payrollImportService,
  formatMoney,
  PAYROLL_CONFIG,
} from 'src/services/payroll';
import type { PayrollImportWithRows } from 'src/types/payroll.types';
import { invalidateStudentFinancialQueries } from 'src/utils/queryInvalidation';
import { CsvDropzone } from './components/CsvDropzone';
import { PaySummaryCards, WizardStepper } from './components/PaySummaryCards';
import {
  PayrollRowsTable,
  isPayableStatus,
  isProblemStatus,
  RowLegend,
} from './components/PayrollRowsTable';
import { Tabs, Tab } from '@mui/material';

function toIsoDate(d: Date | null): string {
  if (!d) return '';
  return format(d, 'yyyy-MM-dd');
}

export default function PayrollImportWizard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [startDate, setStartDate] = useState<Date | null>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 13);
    return d;
  });
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [note, setNote] = useState('');
  const [taxRatePct, setTaxRatePct] = useState(String(PAYROLL_CONFIG.taxRate * 100));
  const [fileName, setFileName] = useState<string | null>(null);
  const [csvText, setCsvText] = useState<string | null>(null);
  const [preview, setPreview] = useState<PayrollImportWithRows | null>(null);
  const [tab, setTab] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number; current?: string } | null>(
    null
  );

  const taxRate = useMemo(() => {
    const n = Number(taxRatePct);
    if (!Number.isFinite(n) || n < 0) return PAYROLL_CONFIG.taxRate;
    return n / 100;
  }, [taxRatePct]);

  const createPreviewMutation = useMutation({
    mutationFn: async () => {
      if (!csvText || !fileName) throw new Error('Upload a CSV first');
      if (!startDate || !endDate) throw new Error('Select a pay period');
      if (startDate > endDate) throw new Error('Pay period start must be before end');
      return payrollImportService.createPreview({
        payPeriodStart: toIsoDate(startDate),
        payPeriodEnd: toIsoDate(endDate),
        taxRate,
        note: note.trim() || undefined,
        fileName,
        csvText,
        uploadedBy: user?.id || null,
      });
    },
    onSuccess: (data) => {
      setPreview(data);
      setStep(1);
      setTab(0);
      toast.success(`Preview ready — ${data.totals.matched_count} students ready to pay`);
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Failed to create payroll preview');
    },
  });

  const approveMutation = useMutation({
    mutationFn: async () => {
      if (!preview) throw new Error('No preview');
      return payrollImportService.approveImport(preview.id, {
        onProgress: (done, total, current) => setProgress({ done, total, current }),
      });
    },
    onSuccess: async (result) => {
      setConfirmOpen(false);
      setProgress(null);
      if (result.failed === 0) {
        toast.success(`Deposited ${result.posted} paycheck${result.posted === 1 ? '' : 's'}`);
      } else {
        toast.warning(`Posted ${result.posted}, ${result.failed} failed — you can retry failures`);
      }
      setStep(2);
      const refreshed = await payrollImportService.getImport(preview!.id);
      setPreview(refreshed);

      await invalidateStudentFinancialQueries(queryClient, {
        extraKeys: [['payroll-imports']],
      });
    },
    onError: (err: any) => {
      setProgress(null);
      toast.error(err?.message || 'Approve failed');
    },
  });

  const anomalyRows = useMemo(
    () => preview?.rows.filter((r) => r.anomalies && r.anomalies.length > 0) || [],
    [preview]
  );

  const resolveAnomalyMutation = useMutation({
    mutationFn: async ({
      rowId,
      periodName,
      resolution,
    }: {
      rowId: string;
      periodName: string;
      resolution: 'keep_both' | 'keep_first' | 'keep_second' | 'average';
    }) => {
      if (!preview) throw new Error('No preview');
      return payrollImportService.updateRowAnomalyResolution(preview.id, rowId, periodName, resolution);
    },
    onSuccess: (updatedPreview) => {
      setPreview(updatedPreview);
      toast.success('Period conflict resolution applied');
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Failed to apply resolution');
    },
  });

  const payableRows = useMemo(
    () => preview?.rows.filter((r) => isPayableStatus(r.match_status) || r.match_status === 'posted' || r.match_status === 'post_failed') || [],
    [preview]
  );
  const readyRows = useMemo(
    () => preview?.rows.filter((r) => isPayableStatus(r.match_status)) || [],
    [preview]
  );
  const problemRows = useMemo(
    () => preview?.rows.filter((r) => isProblemStatus(r.match_status)) || [],
    [preview]
  );
  const tableRows = tab === 0 ? (step === 2 ? payableRows : readyRows) : problemRows;

  const canPreview = Boolean(csvText && startDate && endDate);

  return (
    <Page title="Scholar Dollar Pay Import">
      <Container
        maxWidth={false}
        sx={{ width: '100%', px: 1, mx: 0 }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1}
          sx={{ mb: 1.5 }}
        >
          <Box>
            <Button
              startIcon={<Icon icon="mdi:arrow-left" />}
              onClick={() => navigate('/admin/payroll')}
              sx={{ textTransform: 'none', mb: 0.25, color: 'text.secondary', p: 0, fontSize: '0.75rem' }}
            >
              Scholar Dollar Pay
            </Button>
            <Typography variant="h5" fontWeight={800} color="#1A202C">
              Import Scholar Dollar Pay
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Upload grades → preview net pay → deposit Scholar Dollars in one click
            </Typography>
          </Box>
          <Button
            variant="text"
            startIcon={<Icon icon="mdi:download" />}
            sx={{ textTransform: 'none', fontWeight: 600 }}
            onClick={() => downloadTextFile('demo-scholar-dollar-pay-template.csv', PAYROLL_CSV_TEMPLATE)}
          >
            Download pay template
          </Button>
        </Stack>

        <WizardStepper steps={['Upload & period', 'Preview', 'Done']} activeStep={step} />

        {step === 0 && (
          <Card elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2.5, border: '1px solid #E2E8F0' }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Stack spacing={{ xs: 2, sm: 2.25 }}>
                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
                    1. Pay period
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                    Students can only be paid once per pay period. Pick the same dates your school uses in Sheets.
                  </Typography>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1.25}
                    useFlexGap
                    flexWrap="wrap"
                    alignItems={{ md: 'flex-start' }}
                  >
                    <DatePicker
                      label="Period start"
                      value={startDate}
                      onChange={setStartDate}
                      slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                      sx={{ flex: 1, minWidth: { md: 180 } }}
                    />
                    <DatePicker
                      label="Period end"
                      value={endDate}
                      onChange={setEndDate}
                      slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                      sx={{ flex: 1, minWidth: { md: 180 } }}
                    />
                    <TextField
                      label="Tax rate %"
                      value={taxRatePct}
                      onChange={(e) => setTaxRatePct(e.target.value)}
                      helperText="Default 10% — stored on each paycheck"
                      size="small"
                      sx={{ flex: 0.7, minWidth: { md: 150 } }}
                    />
                    <TextField
                      label="Optional note"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="e.g. Biweekly payroll week 3"
                      size="small"
                      sx={{ flex: 1.35, minWidth: { md: 220 } }}
                    />
                  </Stack>
                </Box>

                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 0.75 }}>
                    2. Upload CSV
                  </Typography>
                  <CsvDropzone
                    fileName={fileName}
                    onFile={(file, text) => {
                      setFileName(file.name);
                      setCsvText(text);
                    }}
                  />
                </Box>

                <Alert severity="info" icon={<Icon icon="mdi:lightbulb-on-outline" width={22} />} sx={{ borderRadius: 2 }}>
                  Blank periods count as 0. Pay = sum of Periods 1–8 ($1 per %), then {taxRatePct || 10}% tax is
                  withheld. Only <strong>net</strong> is deposited; tax is saved for Tax Day.
                </Alert>

                <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
                  <Button
                    variant="contained"
                    size="large"
                    disabled={!canPreview || createPreviewMutation.isPending}
                    onClick={() => createPreviewMutation.mutate()}
                    startIcon={
                      createPreviewMutation.isPending ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <Icon icon="mdi:eye-check-outline" />
                      )
                    }
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      borderRadius: 2,
                      px: { xs: 2, sm: 2.5 },
                      bgcolor: '#297041',
                      '&:hover': { bgcolor: '#1f5833' },
                    }}
                  >
                    {createPreviewMutation.isPending ? 'Matching students…' : 'Preview pay file'}
                  </Button>
                </Stack>
              </Stack>
            </LocalizationProvider>
          </Card>
        )}

        {step >= 1 && preview && (
          <Stack spacing={2.5}>
            <PaySummaryCards
              matched={preview.totals.matched_count}
              problems={preview.totals.error_count}
              warnings={preview.totals.warning_count}
              totalGross={preview.totals.total_gross}
              totalTax={preview.totals.total_tax}
              totalNet={preview.totals.total_net}
            />

            {anomalyRows.length > 0 && (
              <Alert
                severity="warning"
                icon={<Icon icon="mdi:alert-circle-outline" width={22} />}
                sx={{ borderRadius: 2 }}
              >
                <strong>{anomalyRows.length} Scholar{anomalyRows.length === 1 ? '' : 's'} with Period Conflicts Detected:</strong>{' '}
                Multiple courses were listed in the same period (e.g. A/B block schedules). The system has automatically defaulted to paying both courses as 3A/3B (or deduplicating identical grades). You can review or adjust any decision below before approving.
              </Alert>
            )}

            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
              <Box
                sx={{
                  px: 2.5,
                  py: 1.5,
                  bgcolor: alpha('#297041', 0.03),
                  borderBottom: '1px solid #E2E8F0',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  <Typography fontWeight={700}>
                    {preview.file_name} · {preview.pay_period_key.replace('_', ' → ')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Tax rate {(preview.tax_rate * 100).toFixed(1)}%
                    {preview.note ? ` · ${preview.note}` : ''}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  {problemRows.length > 0 && (
                    <Button
                      size="small"
                      startIcon={<Icon icon="mdi:download" />}
                      sx={{ textTransform: 'none' }}
                      onClick={() =>
                        downloadTextFile(
                          `payroll-problems-${preview.id.slice(0, 8)}.csv`,
                          payrollImportService.exportProblemRowsCsv(problemRows)
                        )
                      }
                    >
                      Export problems
                    </Button>
                  )}
                </Stack>
              </Box>

              <Box sx={{ px: 2, pt: 1 }}>
                <Tabs
                  value={tab}
                  onChange={(_, v) => setTab(v)}
                  sx={{
                    minHeight: 48,
                    '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 48 },
                  }}
                >
                  <Tab
                    label={
                      step === 2
                        ? `Posted / payable (${payableRows.length})`
                        : `Ready (${readyRows.length})`
                    }
                  />
                  <Tab label={`Problems (${problemRows.length})`} />
                </Tabs>
              </Box>

              <Box sx={{ px: 2, pb: 1 }}>
                <RowLegend />
              </Box>

              <PayrollRowsTable
                rows={tableRows}
                emptyMessage={tab === 0 ? 'No payable rows' : 'No problem rows — nice work'}
                onResolveAnomaly={(rowId, periodName, resolution) => {
                  resolveAnomalyMutation.mutate({ rowId, periodName, resolution });
                }}
              />

              <Box
                sx={{
                  p: { xs: 1.75, sm: 2.25 },
                  borderTop: '1px solid #E2E8F0',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  bgcolor: alpha('#EDB42E', 0.04),
                }}
              >
                <Typography fontWeight={700}>
                  {step === 2
                    ? `Status: ${preview.status}`
                    : `${readyRows.length} students · Gross ${formatMoney(preview.totals.total_gross)} · Tax ${formatMoney(
                        preview.totals.total_tax
                      )} · Net ${formatMoney(preview.totals.total_net)}`}
                </Typography>

                <Stack direction="row" spacing={1.5}>
                  {step === 1 && (
                    <>
                      <Button
                        sx={{ textTransform: 'none' }}
                        onClick={() => {
                          setStep(0);
                          setPreview(null);
                        }}
                      >
                        Start over
                      </Button>
                      <Button
                        variant="contained"
                        disabled={readyRows.length === 0 || approveMutation.isPending}
                        onClick={() => setConfirmOpen(true)}
                        startIcon={<Icon icon="mdi:cash-fast" />}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          borderRadius: 2,
                          bgcolor: '#EDB42E',
                          color: '#1A1A1A',
                          '&:hover': { bgcolor: '#d9a429' },
                        }}
                      >
                        Approve & deposit
                      </Button>
                    </>
                  )}
                  {step === 2 && (
                    <>
                      {preview.rows.some((r) => r.match_status === 'post_failed') && (
                        <Button
                          variant="outlined"
                          sx={{ textTransform: 'none', borderRadius: 2 }}
                          onClick={() => {
                            setConfirmOpen(true);
                          }}
                        >
                          Retry failures
                        </Button>
                      )}
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          borderRadius: 2,
                          bgcolor: '#297041',
                        }}
                        onClick={() => navigate(`/admin/payroll/imports/${preview.id}`)}
                      >
                        View import detail
                      </Button>
                      <Button
                        variant="outlined"
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          borderRadius: 2,
                          color: '#805AD5',
                          borderColor: '#805AD5',
                          '&:hover': { borderColor: '#6B46C1', bgcolor: alpha('#805AD5', 0.04) },
                        }}
                        onClick={() => navigate('/admin/payroll/tax-day')}
                      >
                        View Tax Day YTD
                      </Button>
                      <Button sx={{ textTransform: 'none' }} onClick={() => navigate('/admin/payroll')}>
                        Back to hub
                      </Button>
                    </>
                  )}
                </Stack>
              </Box>
            </Card>

            {step === 2 && preview.status === 'approved' && (
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                Payroll posted. Students will see Scholar Dollar credits, and each transaction stores a full
                paycheck snapshot for stubs and Tax Day.
              </Alert>
            )}
          </Stack>
        )}

        <Dialog open={confirmOpen} onClose={() => !approveMutation.isPending && setConfirmOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>Confirm Scholar Dollar Pay & Tax Withholding</DialogTitle>
          <DialogContent>
            {preview && (
              <Stack spacing={2} sx={{ pt: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  You are approving payroll and recording Tax Day withholdings for{' '}
                  <strong>{readyRows.length || preview.totals.matched_count}</strong> student
                  {(readyRows.length || preview.totals.matched_count) === 1 ? '' : 's'} across pay period{' '}
                  <strong>{preview.pay_period_key.replace('_', ' → ')}</strong>.
                </Typography>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                    gap: 1.5,
                  }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      p: 1.75,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: alpha('#2B6CB0', 0.25),
                      bgcolor: alpha('#2B6CB0', 0.04),
                    }}
                  >
                    <Typography variant="caption" fontWeight={700} color="#2B6CB0" sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                      Total Gross
                    </Typography>
                    <Typography variant="h6" fontWeight={800} color="#2B6CB0">
                      {formatMoney(preview.totals.total_gross)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.675rem' }}>
                      Earned grade points
                    </Typography>
                  </Card>

                  <Card
                    elevation={0}
                    sx={{
                      p: 1.75,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: alpha('#805AD5', 0.25),
                      bgcolor: alpha('#805AD5', 0.04),
                    }}
                  >
                    <Typography variant="caption" fontWeight={700} color="#805AD5" sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                      Tax ({(preview.tax_rate * 100).toFixed(0)}%)
                    </Typography>
                    <Typography variant="h6" fontWeight={800} color="#805AD5">
                      {formatMoney(preview.totals.total_tax)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.675rem' }}>
                      Recorded for Tax Day
                    </Typography>
                  </Card>

                  <Card
                    elevation={0}
                    sx={{
                      p: 1.75,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: alpha('#297041', 0.25),
                      bgcolor: alpha('#297041', 0.04),
                    }}
                  >
                    <Typography variant="caption" fontWeight={700} color="#297041" sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                      Net Deposited
                    </Typography>
                    <Typography variant="h6" fontWeight={800} color="#297041">
                      {formatMoney(preview.totals.total_net)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.675rem' }}>
                      To scholar wallets
                    </Typography>
                  </Card>
                </Box>

                <Alert
                  severity="info"
                  icon={<Icon icon="mdi:information-outline" width={22} />}
                  sx={{ borderRadius: 2 }}
                >
                  <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                    Net pay will be deposited directly into student checking balances (savings auto-allocation rules apply).
                    Tax withholdings are permanently stored in student paycheck records for Tax Day.
                  </Typography>
                </Alert>

                {progress && (
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                      Posting {progress.done} / {progress.total}
                      {progress.current ? ` · ${progress.current}` : ''}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={progress.total ? (progress.done / progress.total) * 100 : 0}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: '#E2E8F0',
                        '& .MuiLinearProgress-bar': { bgcolor: '#297041' },
                      }}
                    />
                  </Box>
                )}
              </Stack>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button
              disabled={approveMutation.isPending}
              onClick={() => setConfirmOpen(false)}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={approveMutation.isPending}
              onClick={() => approveMutation.mutate()}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 2,
                bgcolor: '#297041',
                '&:hover': { bgcolor: '#1f5833' },
              }}
              startIcon={
                approveMutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined
              }
            >
              {approveMutation.isPending ? 'Depositing…' : 'Yes, deposit paychecks'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}
